import React from 'react';
import { cn, formatNumber, formatPercent, calculateGrowth } from '../../lib/utils';
import { TrendingUp, TrendingDown } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

export const KPICard = ({ 
    title, 
    value, 
    previousValue,
    icon: Icon, 
    color = 'default',
    tooltip,
    className,
    delay = 0
}) => {
    const growth = calculateGrowth(value, previousValue);
    const isPositive = parseFloat(growth) >= 0;

    const colorStyles = {
        default: 'border-line-default',
        red: 'border-brand-red/30 kpi-glow',
        linkedin: 'border-channel-linkedin/30',
        email: 'border-channel-email/30',
        ads: 'border-channel-ads/30',
        inbound: 'border-channel-inbound/30',
    };

    const iconColors = {
        default: 'text-brand-red',
        red: 'text-brand-red',
        linkedin: 'text-channel-linkedin',
        email: 'text-channel-email',
        ads: 'text-channel-ads',
        inbound: 'text-channel-inbound',
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div 
                        className={cn(
                            'kpi-card card-base card-hover p-6',
                            colorStyles[color],
                            className
                        )}
                        style={{ animationDelay: `${delay}ms` }}
                        data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className={cn(
                                'p-2 rounded-lg',
                                color === 'default' ? 'bg-brand-red/10' : `bg-${color}/10`
                            )}>
                                {Icon && <Icon className={cn('w-5 h-5', iconColors[color])} />}
                            </div>
                            {previousValue !== undefined && (
                                <div className={cn(
                                    'flex items-center gap-1 text-xs font-medium',
                                    isPositive ? 'text-status-success' : 'text-status-error'
                                )}>
                                    {isPositive ? (
                                        <TrendingUp className="w-3 h-3" />
                                    ) : (
                                        <TrendingDown className="w-3 h-3" />
                                    )}
                                    {Math.abs(growth)}%
                                </div>
                            )}
                        </div>
                        <p className="text-content-secondary text-sm font-medium uppercase tracking-wide mb-1">
                            {title}
                        </p>
                        <p className="text-3xl font-heading font-bold text-white">
                            {formatNumber(value)}
                        </p>
                    </div>
                </TooltipTrigger>
                {tooltip && (
                    <TooltipContent 
                        className="bg-surface-card border-line-default text-white"
                    >
                        {tooltip}
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
};

export const MetricRow = ({ label, value, total, color = '#FF3B30' }) => {
    const percentage = total ? (value / total) * 100 : 0;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
                <span className="text-content-secondary">{label}</span>
                <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{formatNumber(value)}</span>
                    <span className="text-content-muted">({formatPercent(percentage)})</span>
                </div>
            </div>
            <div className="progress-bar">
                <div 
                    className="progress-bar-fill"
                    style={{ 
                        width: `${percentage}%`,
                        background: color
                    }}
                />
            </div>
        </div>
    );
};

export default KPICard;
